import Card from "components/card";
import React from "react";
import Item from "./Item";
import { useDropzone, Accept } from "react-dropzone";
import { toast } from "react-hot-toast";
import { MdFileUpload } from "react-icons/md";
import { SortableItem } from "./SortableItem";
import { SmartPointerSensor } from "./usePointerSensor";
import {
  SortableContext,
  arrayMove,
  rectSortingStrategy,
  sortableKeyboardCoordinates,
} from "@dnd-kit/sortable";
import {
  DndContext,
  useSensors,
  closestCenter,
  useSensor,
  KeyboardSensor,
  TouchSensor,
  DragOverlay,
  DragStartEvent,
  DragEndEvent,
} from "@dnd-kit/core";

export interface IItem {
  id: number;
  file?: File;
  imageUrl?: string;
  path?: string;
}

type DropzoneFileProps = {
  text?: string;
  files: File[];
  accept: Accept;
  maxSize: number;
  maxFiles: number;
  dropFile: (files: File[]) => void;
  removeFile: (files: File[]) => void;
  arrangeFiles: (files: File[]) => void;
  changeFile: (files: File[]) => void;
};

const DropzoneFile: React.FC<DropzoneFileProps> = (props) => {
  const {
    text,
    files,
    accept,
    maxSize,
    maxFiles,
    dropFile,
    removeFile,
    arrangeFiles,
    changeFile,
  } = props;
  const sensors = useSensors(
    useSensor(SmartPointerSensor, {
      onActivation: () => {
        return;
      },
    }),
    useSensor(TouchSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );
  const [items, setItems] = React.useState<IItem[]>([]);
  const [replaceItem, setReplaceItem] = React.useState<{
    id: number;
    file: File;
  }>(null);
  const [activeItem, setActiveItem] = React.useState<IItem>();

  const filesSpreader = (files: File[]) => {
    files?.forEach((file) => {
      const reader = new FileReader();
      reader.onload = () => {
        setItems((prev) => [
          ...prev,
          {
            id: Math.floor(Math.random() * 1000000),
            file,
            imageUrl: reader.result as string,
          },
        ]);
      };
      reader.readAsDataURL(file);
    });
  };

  const onDrop = React.useCallback(
    (acceptedFiles: File[]) => {
      if (acceptedFiles.length > 0) {
        if (
          files.length > 0 &&
          files.length + acceptedFiles.length <= maxFiles
        ) {
          const newFiles = [...files, ...acceptedFiles];
          dropFile(newFiles);
        } else {
          dropFile(acceptedFiles);
        }

        filesSpreader(acceptedFiles);
      }
    },
    [files, maxFiles, dropFile]
  );

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDrop,
    accept,
    maxSize,
    maxFiles,
    onError: (err: Error) => {
      console.error(err);
      toast(`Terjadi masalah saat mengambil file!`, {
        icon: "❌",
      });
    },
    onDropRejected: (fileRejections) => {
      fileRejections[0].errors[0].code === "file-too-large" &&
        toast(`Ukuran file tidak boleh lebih dari ${maxSize / 1000000} KB!`, {
          icon: "📐",
        });
      fileRejections[0].errors[0].code === "too-many-files" &&
        toast(`File tidak boleh lebih dari ${maxFiles}!`, { icon: "📚" });
    },
    // onDropAccepted: (files, _event) => {
    //   onChangeFile(replaceItem?.id, files[0]);
    // },
  });

  const handleDragStart = (event: DragStartEvent) => {
    const { active } = event;
    setActiveItem(items.find((item) => item.id === active.id));
  };

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) return;

    const activeItem = items.find((item) => item.id === active.id);
    const overItem = items.find((item) => item.id === over.id);

    if (!activeItem || !overItem) return;

    const activeIndex = items.findIndex((item) => item.id === active.id);
    const overIndex = items.findIndex((item) => item.id === over.id);

    if (activeIndex !== overIndex) {
      const res = arrayMove(items, activeIndex, overIndex);
      setItems(res);
      arrangeFiles(
        res.map((item) => {
          return item.file as File;
        })
      );
    }
    setActiveItem(undefined);
  };

  const handleDragCancel = () => {
    setActiveItem(undefined);
  };

  const onRemove = (id: number) => {
    const newItems = items.filter((item) => item.id !== id);
    setItems((prev) => prev.filter((item) => item.id !== id));
    removeFile(
      newItems.map((item) => {
        return item.file as File;
      })
    );
  };

  const onChangeFile = (id: number, file: File) => {
    if (id) {
      setReplaceItem({ id: id, ...replaceItem });
      open();
    }
    if (file) setReplaceItem({ ...replaceItem, file });

    if (replaceItem?.id && file) {
      const newItems = items.map((item) => {
        if (item.id === id) {
          return {
            ...item,
            file: file,
            imageUrl: URL.createObjectURL(file),
          };
        }
        return item;
      });
      setItems(newItems);
      changeFile(
        newItems.map((item) => {
          return item.file as File;
        })
      );
      setReplaceItem(null);
    }
  };

  return (
    <Card className="relative grid h-full w-full grid-cols-1 gap-3 rounded-xl bg-white bg-clip-border p-3 font-dm shadow-3xl shadow-shadow-500 dark:!bg-navy-800 dark:shadow-none 2xl:grid-cols-11">
      {items.length < maxFiles && (
        <div className="col-span-full h-56 w-full overflow-hidden rounded-xl bg-lightPrimary dark:!bg-navy-700">
          <div
            className="relative flex h-56 w-full cursor-pointer flex-col items-center justify-center rounded-xl border-[2px] border-dashed border-gray-200 py-3 text-center dark:!border-navy-700 lg:pb-0"
            {...getRootProps()}
          >
            <input
              {...getInputProps()}
              className="absolute left-0 top-0 !block h-56 w-full cursor-pointer bg-transparent opacity-0"
            />
            <MdFileUpload className="text-[80px] text-brand-300 dark:text-white" />
            <h4 className="text-xl font-bold text-brand-300 dark:text-white">
              {text}
            </h4>
            {isDragActive ? (
              <p className="text-sm font-medium text-gray-600">
                Taruh file di sini ...
              </p>
            ) : (
              <div className="mb-2 mt-1">
                <p className="text-sm font-medium text-gray-600">
                  Tarik & taruh file di sini, atau klik untuk memilih file
                </p>
                <p className="text-sm font-medium text-gray-600">
                  Hanya file dengan ekstensi:{" "}
                  <span className="text-brand-400">
                    {Object.values(accept).map((types) => {
                      return types.map((type, index) => {
                        if (index < types.length - 1) return type + ", ";
                        else return type + " ";
                      });
                    })}
                  </span>
                </p>
              </div>
            )}
          </div>
        </div>
      )}
      {files.length > 0 && items && (
        <div className="col-span-full">
          <div
            className={`mt-2 grid gap-3
            ${files.length >= 2 ? "grid-cols-3" : "gird-cols-" + files.length}
          `}
          >
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragStart={handleDragStart}
              onDragEnd={handleDragEnd}
              onDragCancel={handleDragCancel}
            >
              <SortableContext items={items} strategy={rectSortingStrategy}>
                {items.map((item) => (
                  <SortableItem
                    key={item.id}
                    item={item}
                    onRemove={onRemove}
                    onChangeFile={onChangeFile}
                    open={open}
                  />
                ))}
              </SortableContext>
              <DragOverlay adjustScale style={{ transformOrigin: "0 0 " }}>
                {activeItem ? (
                  <Item
                    onRemove={onRemove}
                    open={open}
                    onChangeFile={onChangeFile}
                    item={activeItem}
                  />
                ) : null}
              </DragOverlay>
            </DndContext>
          </div>
        </div>
      )}
    </Card>
  );
};

export default DropzoneFile;
